import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const DEFAULT_IDLE_LIMIT_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 15000;
const ACTIVITY_THROTTLE_MS = 5000;
const VISIBLE_HEARTBEAT_MS = 60000;

function resolveIdleLimitMs(idleLimitMinutes) {
  const parsed = Number(idleLimitMinutes);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed * 60 * 1000;
  }
  return DEFAULT_IDLE_LIMIT_MS;
}

function IdleLogoutGuard({ children, enabled, authKey, activityKey, clearKeys, redirectTo, idleLimitMinutes }) {
  const navigate = useNavigate();
  const location = useLocation();
  const lastMarkedRef = useRef(0);
  const hasLoggedOutRef = useRef(false);
  const idleLimitMs = resolveIdleLimitMs(idleLimitMinutes);

  useEffect(() => {
    if (!enabled || !localStorage.getItem(authKey)) {
      return undefined;
    }

    hasLoggedOutRef.current = false;

    const markActivity = () => {
      if (document.visibilityState === "hidden") {
        return;
      }

      const now = Date.now();
      if (now - lastMarkedRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }

      lastMarkedRef.current = now;
      localStorage.setItem(activityKey, String(now));
    };

    const performLogout = () => {
      if (hasLoggedOutRef.current) {
        return;
      }

      hasLoggedOutRef.current = true;
      clearKeys.forEach((key) => localStorage.removeItem(key));
      localStorage.removeItem(activityKey);
      localStorage.setItem("idleLogoutSync", String(Date.now()));
      navigate(redirectTo, { replace: true, state: { idleLoggedOut: true } });
    };

    const checkIdleTimeout = () => {
      if (!localStorage.getItem(authKey)) {
        return;
      }

      const lastActive = Number(localStorage.getItem(activityKey) || Date.now());
      if (Date.now() - lastActive >= idleLimitMs) {
        performLogout();
      }
    };

    const handleStorageChange = (event) => {
      if (event.key === "idleLogoutSync" || event.key === authKey) {
        if (!localStorage.getItem(authKey)) {
          navigate(redirectTo, { replace: true, state: { idleLoggedOut: true } });
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        markActivity();
      }
    };

    markActivity();
    const timer = window.setInterval(checkIdleTimeout, CHECK_INTERVAL_MS);
    const visibleHeartbeat = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        markActivity();
      }
    }, VISIBLE_HEARTBEAT_MS);
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "wheel",
      "touchstart",
      "touchmove",
      "click",
      "focus",
    ];

    events.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.clearInterval(timer);
      window.clearInterval(visibleHeartbeat);
      events.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [activityKey, authKey, clearKeys, enabled, idleLimitMs, navigate, redirectTo, location.pathname]);

  return children;
}

export default IdleLogoutGuard;
